import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useMemo } from "react";

// Cache keys centralizados
export const CACHE_KEYS = {
  GPT_AGENTS: 'gpt-agents',
  PROMPTS: 'prompts-tools',
  SPREADSHEETS: 'spreadsheets-tools',
  EBOOKS: 'ebooks-tools',
  TOOL_CATEGORIES: 'tool-categories',
  PRICING_PLANS: 'pricing-plans',
  TEMPLATES: 'smartcheck-templates',
} as const;

// Configurações de cache otimizadas por tipo de dado
const CACHE_CONFIG = {
  // Dados estáticos - cache longo (30 min)
  static: {
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  },
  // Dados semi-estáticos - cache médio (10 min)
  semiStatic: {
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  },
  // Dados dinâmicos - cache curto (2 min)
  dynamic: {
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
} as const;

// Hook para GPT Agents com cache otimizado
export const useGPTAgentsCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.GPT_AGENTS],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gpt_agents')
        .select('*')
        .eq('is_active', true)
        .order('titulo');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.semiStatic,
  });
};

// Hook para Prompts com cache otimizado
export const usePromptsCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.PROMPTS],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prompts_tools')
        .select('*')
        .eq('is_active', true)
        .order('title');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.semiStatic,
  });
};

// Hook para Spreadsheets com cache otimizado
export const useSpreadsheetsCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.SPREADSHEETS],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('spreadsheets_tools')
        .select('*')
        .eq('is_active', true)
        .order('title');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.semiStatic,
  });
};

// Hook para Ebooks com cache otimizado
export const useEbooksCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.EBOOKS],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ebooks_tools')
        .select('*')
        .eq('is_active', true)
        .order('title');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.static,
  });
};

// Hook para Tool Categories com cache longo
export const useToolCategoriesCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.TOOL_CATEGORIES],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tool_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.static,
  });
};

// Hook para Pricing Plans com cache longo
export const usePricingPlansCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.PRICING_PLANS],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pricing_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.static,
  });
};

// Hook para SmartCheck Templates com cache
export const useSmartCheckTemplatesCache = () => {
  return useQuery({
    queryKey: [CACHE_KEYS.TEMPLATES],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('smartcheck_templates')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      
      if (error) throw error;
      return data || [];
    },
    ...CACHE_CONFIG.semiStatic,
  });
};

// Hook centralizado para invalidar caches específicos
export const useCacheInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateTools = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.GPT_AGENTS] });
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROMPTS] });
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.SPREADSHEETS] });
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.EBOOKS] });
  }, [queryClient]);

  const invalidateCategories = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.TOOL_CATEGORIES] });
  }, [queryClient]);

  const invalidatePlans = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PRICING_PLANS] });
  }, [queryClient]);

  const invalidateTemplates = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.TEMPLATES] });
  }, [queryClient]);

  const invalidateAll = useCallback(() => {
    invalidateTools();
    invalidateCategories();
    invalidatePlans();
    invalidateTemplates();
  }, [invalidateTools, invalidateCategories, invalidatePlans, invalidateTemplates]);

  return {
    invalidateTools,
    invalidateCategories,
    invalidatePlans,
    invalidateTemplates,
    invalidateAll,
  };
};

// Hook combinado para carregar todas as ferramentas de uma vez
export const useAllToolsCache = () => {
  const agents = useGPTAgentsCache();
  const prompts = usePromptsCache();
  const spreadsheets = useSpreadsheetsCache();
  const ebooks = useEbooksCache();
  const categories = useToolCategoriesCache();

  const isLoading = agents.isLoading || prompts.isLoading || 
                    spreadsheets.isLoading || ebooks.isLoading || 
                    categories.isLoading;

  const isError = agents.isError || prompts.isError || 
                  spreadsheets.isError || ebooks.isError || 
                  categories.isError;

  // Memoizar dados combinados
  const allTools = useMemo(() => ({
    agents: agents.data || [],
    prompts: prompts.data || [],
    spreadsheets: spreadsheets.data || [],
    ebooks: ebooks.data || [],
    categories: categories.data || [],
  }), [agents.data, prompts.data, spreadsheets.data, ebooks.data, categories.data]);

  // Contagem total
  const totalCount = useMemo(() => 
    allTools.agents.length + 
    allTools.prompts.length + 
    allTools.spreadsheets.length + 
    allTools.ebooks.length,
  [allTools]);

  return {
    ...allTools,
    isLoading,
    isError,
    totalCount,
  };
};

// Prefetch de dados para navegação antecipada
export const usePrefetchTools = () => {
  const queryClient = useQueryClient();

  const prefetchAll = useCallback(async () => {
    // Prefetch em paralelo
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: [CACHE_KEYS.GPT_AGENTS],
        queryFn: async () => {
          const { data } = await supabase
            .from('gpt_agents')
            .select('*')
            .eq('is_active', true);
          return data || [];
        },
        ...CACHE_CONFIG.semiStatic,
      }),
      queryClient.prefetchQuery({
        queryKey: [CACHE_KEYS.PROMPTS],
        queryFn: async () => {
          const { data } = await supabase
            .from('prompts_tools')
            .select('*')
            .eq('is_active', true);
          return data || [];
        },
        ...CACHE_CONFIG.semiStatic,
      }),
      queryClient.prefetchQuery({
        queryKey: [CACHE_KEYS.SPREADSHEETS],
        queryFn: async () => {
          const { data } = await supabase
            .from('spreadsheets_tools')
            .select('*')
            .eq('is_active', true);
          return data || [];
        },
        ...CACHE_CONFIG.semiStatic,
      }),
    ]);
  }, [queryClient]);

  return { prefetchAll };
};
